/**
 * UNA FOTO CON LAS DOS CARAS, partida en dos.
 *
 * La gente fotografía o escanea las dos caras juntas: una arriba de la otra, o
 * una al lado de la otra. Hasta ahora eso entraba como "frente", el lector
 * ubicaba UNA de las dos tarjetas y la otra se perdía — y el modal seguía
 * pidiendo el dorso que ya estaba en la misma imagen.
 *
 * Todo lo que hace falta ya existía: el bloque del MRZ dice dónde está una de
 * las tarjetas, y los rótulos impresos dicen cuál de las dos mitades es el
 * frente. Acá se junta eso en un corte.
 *
 * Lo que NO hace: recortar con precisión. Devuelve dos mitades generosas y cada
 * una vuelve a pasar por la cascada completa, que es la que ubica la tarjeta,
 * la endereza y la recorta. Partir de más es barato; partir de menos corta un
 * dato y eso no se recupera.
 */
import { Cara } from "./catalogo";
import { hallazgosDelFrente } from "./frente";
import { orientacionPorMrz } from "./anclajeMrz";
import { Adaptadores, ImagenConMedidas } from "./pipeline";
import { ANCHO_OCR } from "./tuning";
import { Rectangulo } from "./types";
import { PalabraOcr } from "../dniMatch";

export interface CaraSeparada<T extends ImagenConMedidas> {
  imagen: T;
  cara: Cara;
  /** Qué parte de la foto original es. Para el diagnóstico. */
  caja: Rectangulo;
}

export interface Separacion<T extends ImagenConMedidas> {
  caras: CaraSeparada<T>[];
  /**
   * La imagen A LA QUE SE REFIERE EL CORTE. Puede no ser la que entró: si el
   * documento venía acostado hubo que enderezarlo antes de poder cortar, y
   * mostrar la línea sobre la otra sería mostrarla en cualquier lado.
   */
  imagen: T;
  /** Dónde se cortó, en píxeles de `imagen`. Para poder mostrarlo. */
  corte: { vertical: boolean; en: number };
  diagnostico: string[];
}

/**
 * Cuánto más grande que una tarjeta tiene que ser la foto para sospechar que
 * hay dos. Con 1,6 una tarjeta sola con bastante mesa alrededor no dispara:
 * dos tarjetas apiladas ocupan 2 de alto aunque se toquen.
 */
const HAY_LUGAR_PARA_DOS = 1.6;
/** El corte nunca deja una mitad más chica que esto. */
const MITAD_MINIMA = 0.15;
/** Ver el comentario en `separarCaras`: acá dos renglones de MRZ alcanzan. */
const MINIMO_DE_TOKENS_DE_MRZ = 2;

function escalar(r: Rectangulo, f: number): Rectangulo {
  return { x: r.x * f, y: r.y * f, w: r.w * f, h: r.h * f };
}

/** Los rótulos del frente que se leen en un grupo de palabras. Es el voto. */
function pintaDeFrente(palabras: PalabraOcr[]): number {
  return hallazgosDelFrente(palabras).length;
}

/**
 * Parte la foto si trae las dos caras. `null` = es una sola y se lee como hasta
 * ahora.
 */
export async function separarCaras<T extends ImagenConMedidas>(
  original: T,
  ad: Adaptadores<T>
): Promise<Separacion<T> | null> {
  const mirar = async (img: T) => {
    const ancho = Math.min(img.ancho, ANCHO_OCR);
    const paraOcr = ancho === img.ancho ? img : await ad.imagen.escalarAAncho(img, ancho);
    const { palabras } = await ad.ocr.leer(paraOcr);
    return { palabras, factor: img.ancho / paraOcr.ancho };
  };

  // El MRZ es el ancla: renglones paralelos que ninguna otra cosa imita, y que
  // dicen dónde está la tarjeta que los lleva.
  //
  // Acá alcanza con DOS. El corte no se aplica solo —lo confirma quien ve la
  // foto—, así que una sospecha de más no cuesta nada, y una de menos cuesta la
  // cara entera: sobre la foto de las dos caras el OCR perdió la línea 1 del
  // MRZ y con tres el bloque no se reconocía.
  let imagen = original;
  let { palabras, factor: f } = await mirar(imagen);
  let o = orientacionPorMrz(palabras, undefined, MINIMO_DE_TOKENS_DE_MRZ);

  // El documento puede venir acostado, y de hecho VIENE: el cliente gira un
  // cuarto de vuelta toda foto vertical antes de leerla, y una foto de las dos
  // caras apiladas es vertical. Con el MRZ de costado no hay tarjeta que
  // deducir, así que se endereza y se vuelve a mirar. Cuesta una pasada de OCR
  // y solo cuando hace falta.
  if (o && !o.tarjeta && o.cuartosDeVuelta === 90) {
    imagen = await ad.imagen.girar(original, 270);
    ({ palabras, factor: f } = await mirar(imagen));
    o = orientacionPorMrz(palabras, undefined, MINIMO_DE_TOKENS_DE_MRZ);
  }
  if (!o?.tarjeta) return null;

  const conMrz = escalar(o.tarjeta, f);

  // ¿Entra otra tarjeta al lado o debajo? Se mide contra la tarjeta que
  // encontramos, no contra una proporción de la foto: es lo mismo que preguntar
  // "¿sobra lugar para una segunda?".
  const sobraAbajo = imagen.alto - conMrz.h;
  const sobraAlLado = imagen.ancho - conMrz.w;
  const apiladas = imagen.alto > conMrz.h * HAY_LUGAR_PARA_DOS;
  const enFila = imagen.ancho > conMrz.w * HAY_LUGAR_PARA_DOS;
  if (!apiladas && !enFila) return null;
  const vertical = apiladas && (!enFila || sobraAbajo >= sobraAlLado);

  // La otra tarjeta está del lado donde sobra más foto.
  const largo = vertical ? imagen.alto : imagen.ancho;
  const inicio = vertical ? conMrz.y : conMrz.x;
  const fin = inicio + (vertical ? conMrz.h : conMrz.w);
  const otraDespues = largo - fin > inicio;
  const corte = Math.min(
    Math.max(otraDespues ? fin : inicio, largo * MITAD_MINIMA),
    largo * (1 - MITAD_MINIMA)
  );

  const primera: Rectangulo = vertical
    ? { x: 0, y: 0, w: imagen.ancho, h: corte }
    : { x: 0, y: 0, w: corte, h: imagen.alto };
  const segunda: Rectangulo = vertical
    ? { x: 0, y: corte, w: imagen.ancho, h: imagen.alto - corte }
    : { x: corte, y: 0, w: imagen.ancho - corte, h: imagen.alto };

  // Cuál es el frente se decide por los RÓTULOS IMPRESOS, que son los mismos en
  // los tres formatos: Apellido, Nombre, Sexo, Nacionalidad, Documento. El
  // dorso lleva otros —Domicilio, Lugar de nacimiento, CUIL—, así que el que
  // más rótulos de frente tenga es el frente.
  //
  // Se cuentan sobre las palabras que YA leímos, partidas por el corte: no
  // cuesta una pasada más de OCR.
  const corteOcr = corte / f;
  const antes = palabras.filter((w) => (vertical ? w.bbox.y1 : w.bbox.x1) <= corteOcr);
  const despues = palabras.filter((w) => (vertical ? w.bbox.y0 : w.bbox.x0) >= corteOcr);
  const puntajeAntes = pintaDeFrente(antes);
  const puntajeDespues = pintaDeFrente(despues);

  // Que sobre lugar no quiere decir que haya otra tarjeta: puede ser la mesa.
  // Medido: un dorso solo fotografiado en vertical deja 800 px de madera abajo,
  // y sin esta prueba se partía en dos.
  //
  // Una tarjeta se reconoce por lo que sabemos reconocer de una tarjeta: sus
  // rótulos impresos, o su propio bloque de MRZ. Si en la otra mitad no hay
  // ninguna de las dos cosas, no hay otra tarjeta.
  const otras = otraDespues ? despues : antes;
  const hayOtraTarjeta =
    pintaDeFrente(otras) > 0 || !!orientacionPorMrz(otras, undefined, MINIMO_DE_TOKENS_DE_MRZ)?.tarjeta;
  if (!hayOtraTarjeta) return null;

  let primeraEsFrente: boolean;
  let porQue: string;
  if (puntajeAntes !== puntajeDespues) {
    primeraEsFrente = puntajeAntes > puntajeDespues;
    porQue = `rótulos ${puntajeAntes} vs ${puntajeDespues}`;
  } else {
    // Empate —las dos mitades ilegibles, o las dos con los mismos rótulos—: la
    // que lleva el MRZ es el dorso. Vale para los formatos desde 2012, que son
    // todos los vigentes salvo la tarjeta 2009-2012, donde el MRZ está en el
    // frente. Por eso es el desempate y no la regla.
    primeraEsFrente = otraDespues;
    porQue = "empate en los rótulos: la cara del MRZ se toma como dorso";
  }

  const caras: CaraSeparada<T>[] = [];
  for (const [caja, esFrente] of [
    [primera, primeraEsFrente],
    [segunda, !primeraEsFrente],
  ] as [Rectangulo, boolean][]) {
    caras.push({
      imagen: await ad.imagen.recortar(imagen, caja),
      cara: esFrente ? "frente" : "dorso",
      caja,
    });
  }

  return {
    caras,
    imagen,
    corte: { vertical, en: corte },
    diagnostico: [
      `la foto trae las dos caras: corte ${vertical ? "horizontal" : "vertical"} en ${Math.round(corte)}px (${porQue})`,
    ],
  };
}
